import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Badge, Banner, BlockStack, Button, Card, InlineStack, Spinner, Text } from '@shopify/polaris';
import { productsAPI, rentalsAPI } from '../services/api';
import RentalDetailModal from '../components/RentalDetailModal';
import RentalOverviewCard from '../components/RentalOverviewCard';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_MS = 24 * 60 * 60 * 1000;

function dayNumber(value) {
  const date = new Date(value);
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;
}

function monthDays(year, month) {
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= count; day += 1) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function BookingMonth({ year, month, rentals, onOpenRental }) {
  const cells = useMemo(() => monthDays(year, month), [year, month]);
  const today = dayNumber(new Date());

  return (
    <div>
      <Text variant="headingSm" alignment="center">
        {new Date(year, month, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
      </Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', marginTop: 12 }}>
        {WEEKDAYS.map((day) => (
          <div key={day} style={{ padding: '6px 2px', textAlign: 'center' }}>
            <Text variant="bodySm" tone="subdued">{day}</Text>
          </div>
        ))}
        {cells.map((date, index) => {
          if (!date) return <div key={`empty-${index}`} style={{ minHeight: 78, border: '1px solid #ebebeb' }} />;
          const number = dayNumber(date);
          const bookings = rentals.filter(
            (rental) => number >= dayNumber(rental.startDate) && number <= dayNumber(rental.endDate)
          );
          return (
            <div
              key={date.toISOString()}
              style={{
                minHeight: 78,
                padding: 4,
                border: '1px solid #ebebeb',
                background: number === today ? '#f1f8f5' : '#fff',
                minWidth: 0,
              }}
            >
              <Text variant="bodySm" fontWeight={number === today ? 'bold' : undefined}>{date.getDate()}</Text>
              <BlockStack gap="050">
                {bookings.map((rental) => (
                  <button
                    key={rental.id}
                    type="button"
                    onClick={() => onOpenRental(rental)}
                    title={`Open rental #${rental.id}`}
                    style={{
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      padding: '2px 4px',
                      border: 0,
                      borderRadius: 4,
                      background: rental.status === 'overdue' ? '#fee4e2' : '#dff7e5',
                      color: '#202223',
                      cursor: 'pointer',
                      fontSize: 11,
                      textAlign: 'left',
                    }}
                  >
                    #{rental.id}
                  </button>
                ))}
              </BlockStack>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ProductBookings = () => {
  const { shopifyProductId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productTitle, setProductTitle] = useState(location.state?.productTitle || '');
  const [selectedRental, setSelectedRental] = useState(null);
  const today = new Date();
  const [visibleMonth, setVisibleMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const fetchRentals = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await rentalsAPI.getByProduct(shopifyProductId, { includeImages: true });
      setRentals(response.data?.rentals || []);
    } catch (err) {
      setError('Failed to load bookings for this product');
      console.error('Bookings fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [shopifyProductId]);

  const fetchProductTitle = useCallback(async () => {
    if (productTitle) return;
    try {
      const response = await productsAPI.getAll({ isBundle: 'true', limit: 1000 });
      const products = response.data?.products || response.data || [];
      const found = products.find((product) => product.shopifyProductId === shopifyProductId);
      if (found) return setProductTitle(found.title);
      const nonBundleResponse = await productsAPI.getAll({ isBundle: 'false', limit: 1000 });
      const nonBundleProducts = nonBundleResponse.data?.products || nonBundleResponse.data || [];
      const nonBundleFound = nonBundleProducts.find((product) => product.shopifyProductId === shopifyProductId);
      if (nonBundleFound) setProductTitle(nonBundleFound.title);
    } catch (err) {
      // The product ID remains visible when title lookup is unavailable.
    }
  }, [shopifyProductId, productTitle]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  useEffect(() => {
    fetchProductTitle();
  }, [fetchProductTitle]);

  const secondMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text variant="headingLg">Bookings{productTitle ? `: ${productTitle}` : ''}</Text>
            <Text variant="bodyMd" tone="subdued">Product ID: {shopifyProductId}</Text>
          </BlockStack>
          <Button onClick={() => navigate('/products')}>Back to Products</Button>
        </InlineStack>

        {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}

        {loading ? (
          <Card>
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Spinner size="large" />
              <br />
              <Text variant="bodyMd" tone="subdued">Loading bookings...</Text>
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text variant="headingMd">Booking Calendar</Text>
                    <Text variant="bodyMd" tone="subdued">Click a booking number to open its rental details.</Text>
                  </BlockStack>
                  <InlineStack gap="200">
                    <Button onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}>Previous</Button>
                    <Button onClick={() => setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1))}>Today</Button>
                    <Button onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}>Next</Button>
                  </InlineStack>
                </InlineStack>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 24 }}>
                  <BookingMonth
                    year={visibleMonth.getFullYear()}
                    month={visibleMonth.getMonth()}
                    rentals={rentals}
                    onOpenRental={setSelectedRental}
                  />
                  <BookingMonth
                    year={secondMonth.getFullYear()}
                    month={secondMonth.getMonth()}
                    rentals={rentals}
                    onOpenRental={setSelectedRental}
                  />
                </div>
              </BlockStack>
            </Card>

            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd">All Bookings</Text>
                <Badge>{rentals.length}</Badge>
              </InlineStack>
              {rentals.length === 0 ? (
                <Card><Text variant="bodyMd" tone="subdued">No bookings found for this product.</Text></Card>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: 16 }}>
                  {rentals.map((rental) => (
                    <RentalOverviewCard
                      key={rental.id}
                      rental={rental}
                      mode="booking"
                      onView={setSelectedRental}
                    />
                  ))}
                </div>
              )}
            </BlockStack>
          </>
        )}

        <RentalDetailModal
          rental={selectedRental}
          open={Boolean(selectedRental)}
          onClose={() => setSelectedRental(null)}
        />
      </BlockStack>
    </div>
  );
};

export default ProductBookings;
